import { useCallback, useState } from 'react'
import useWeb3 from '../../../hooks/useWeb3'
import { useLazyGetContractByNameQuery } from '../../../modules/web3/api'
import useIsLoggedIn from '../../../hooks/useIsLoggedIn'
import { ethers, Transaction } from 'ethers'
import useNetworkId from '../../../hooks/useNetworkId'
import { formatNumberToFirstDecimalDigit } from '../TokenPage/helpers'
import useEthBalance from '../../../hooks/useWeb3/useEthBalance'

export const MEMECOIN_TOKEN_DECIMALS = 6 // not changing this for the forseable future

const decimals = MEMECOIN_TOKEN_DECIMALS

const useDeployToChain = () => {
  const { getReadOnlyContract, getWriteOnlyContract, provider, signer } =
    useWeb3()
  const { getEthBalance } = useEthBalance()
  const { address } = useIsLoggedIn()
  const { networkId } = useNetworkId()
  const [getContractByName] = useLazyGetContractByNameQuery()

  const [isDeployingToken, setIsDeployingToken] = useState(false)

  const prepareTransaction = useCallback(
    async ({
      initialBuyAmountInEth,
      tokenName,
      tokenSymbol,
    }: {
      initialBuyAmountInEth: number
      tokenName: string
      tokenSymbol: string
    }) => {
      try {
        setIsDeployingToken(true)

        if (!address) throw new Error('No address')

        const { data } = await getContractByName({
          name: 'LiquidityFreeLaunchFactory',
          chainId: networkId,
        })

        if (!data?.contract) throw new Error('No data')

        const {
          contract: { abi: factoryAbi, address: factoryAddress },
        } = data

        if (!factoryAbi) throw new Error('No factoryAbi')
        if (!factoryAddress) throw new Error('No factoryAddress')

        const readOnlyFactoryContract = getReadOnlyContract(
          factoryAddress,
          factoryAbi,
        )
        const writeOnlyFactoryContract = getWriteOnlyContract(
          factoryAddress,
          factoryAbi,
        )

        if (!readOnlyFactoryContract)
          throw new Error('No readOnlyFactoryContract')
        if (!writeOnlyFactoryContract)
          throw new Error('No writeOnlyFactoryContract')

        const initialBuyAmountInWei = ethers.parseUnits(
          initialBuyAmountInEth.toString(),
          18,
        )

        const baseFeeWei = await readOnlyFactoryContract.BASE_SEED_FEE()
        if (!baseFeeWei) throw new Error('Failed to get base fee')

        const balanceInWei = await getEthBalance()
        if (BigInt(baseFeeWei) > BigInt(balanceInWei)) {
          const balanceInEth = ethers.formatEther(balanceInWei)
          return {
            error: `Insufficient funds! (${formatNumberToFirstDecimalDigit(
              balanceInEth,
            )} ETH available; required min.: ${formatNumberToFirstDecimalDigit(
              ethers.formatEther(baseFeeWei),
            )} ETH)`,
          }
        }

        if (initialBuyAmountInWei > 0n) {
          if (initialBuyAmountInWei > BigInt(balanceInWei)) {
            const balanceInEth = ethers.formatEther(balanceInWei)
            return {
              error: `Insufficient funds! (${formatNumberToFirstDecimalDigit(
                balanceInEth,
              )} ETH available)`,
            }
          }

          const maxFeeWei = await readOnlyFactoryContract.MAX_SEED_FEE()
          if (!maxFeeWei) throw new Error('Failed to get max fee')
          if (initialBuyAmountInWei > maxFeeWei) {
            return {
              error: `You're buying more than the max. possible amount of ${formatNumberToFirstDecimalDigit(
                ethers.formatEther(maxFeeWei),
                { decimals },
              )} ETH`,
            }
          }
        }

        const feeWei =
          initialBuyAmountInWei > BigInt(baseFeeWei)
            ? initialBuyAmountInWei +
            // we need to add the fee here, as dev buy is supplementary
            BigInt(baseFeeWei) / 2n
            : BigInt(baseFeeWei)

        const tx =
          await writeOnlyFactoryContract.createLiquidityFreeLaunch.populateTransaction(
            tokenName,
            tokenSymbol,
            {
              value: feeWei,
            },
          )

        const gasEstimate =
          await writeOnlyFactoryContract.createLiquidityFreeLaunch.estimateGas(
            tokenName,
            tokenSymbol,
            {
              value: feeWei,
            },
          )

        //const nonce = await signer.getNonce()
        const { gasPrice } = await provider.getFeeData()

        const populatedTx = {
          ...tx,
          gasLimit: gasEstimate,
          // doesn't work with smart wallet/zerodev
          //nonce: 0,
          gasPrice,
        }

        const unsignedTx = Transaction.from(populatedTx)
        const txHash = ethers.keccak256(unsignedTx.unsignedSerialized)

        return {
          signer,
          txHash,
          populatedTx,
          contractInstance: writeOnlyFactoryContract,
        }
      } catch (error) {
        throw error
      }
    },
    [
      address,
      getContractByName,
      getEthBalance,
      getReadOnlyContract,
      getWriteOnlyContract,
      networkId,
      provider,
      signer,
    ],
  )

  return {
    prepareTransaction,
    isDeployingToken,
  }
}

export default useDeployToChain
